import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, Clock, CircleCheck, AlertTriangle, CheckCheck, Workflow } from "lucide-react";
import { SketchPicker} from "react-color"

const iconOptions = [
  { name: "Clock", component: Clock },
  { name: "CircleCheck", component: CircleCheck },
  { name: "AlertTriangle", component: AlertTriangle },
  { name: "CheckCheck", component: CheckCheck },
  { name: "AlertCircle", component: AlertCircle },
  { name: "Workflow", component: Workflow },
];

const defaultColors = [
  "#64748b", // slate
  "#3b82f6", // blue
  "#f59e0b", // amber
  "#22c55e", // green
  "#ef4444", // red
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#14b8a6", // teal
];

const AddColumnModal = ({ isOpen, onOpenChange, onAddColumn }) => {
  const [columnName, setColumnName] = useState("");
  const [iconName, setIconName] = useState("Clock");
  const [iconColor, setIconColor] = useState("#64748b");
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setColumnName("");
      setIconName("Clock");
      setIconColor("#64748b");
      setError("");
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!columnName.trim()) {
      setError("Column name is required");
      return;
    }
    
    const selectedIcon = iconOptions.find(option => option.name === iconName)?.name;

    onAddColumn({
      title: columnName.trim(),
      icon_name: selectedIcon,
      icon_color: iconColor
    });
    
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add New Column</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="columnName" className="text-right">
                Column Name
              </Label>
              <Input
                id="columnName"
                value={columnName}
                onChange={(e) => setColumnName(e.target.value)}
                className="col-span-3"
                placeholder="Enter column name"
                autoFocus
              />
              {error && (
                <p className="text-red-500 text-sm col-span-3 col-start-2">{error}</p>
              )}
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label className="text-right">Icon</Label>
              <div className="col-span-3 flex flex-wrap gap-2">
                {iconOptions.map((icon) => {
                  const Icon = icon.component;
                  return (
                    <Button
                      key={icon.name}
                      type="button"
                      variant={iconName === icon.name ? "default" : "outline"}
                      className="h-10 w-10 p-2"
                      onClick={() => setIconName(icon.name)}
                    >
                      <Icon color={iconColor} size={18} />
                    </Button>
                  );
                })}
              </div>
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label className="text-right">Color</Label>
              <div className="col-span-3">
                <SketchPicker
                  colors={defaultColors}
                  color={iconColor}
                  onChangeComplete={(color) => setIconColor(color.hex)}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">Create Column</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddColumnModal;